export const AMBIENT_SOUND_OPTIONS = [
  { id: 'rain', label: 'Gentle Rain' },
  { id: 'ocean', label: 'Ocean Waves' },
  { id: 'stream', label: 'Mountain Stream' },
  { id: 'forest', label: 'Forest Morning' },
  { id: 'wind', label: 'Soft Wind' },
  { id: 'fireplace', label: 'Fireplace' },
  { id: 'meditation', label: 'Meditation Bowl' },
  { id: 'night', label: 'Night Crickets' },
  { id: 'piano', label: 'Soft Piano Pad' },
  { id: 'cafe', label: 'Quiet Café' },
  { id: 'white-noise', label: 'White Noise' },
] as const;

export type AmbientSoundType = (typeof AMBIENT_SOUND_OPTIONS)[number]['id'];

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let activeNodes: AudioNode[] = [];
let activeTimers: ReturnType<typeof setTimeout>[] = [];
let currentType: AmbientSoundType | null = null;
let previewMode = false;

function getCtx(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

function trackNode(...nodes: AudioNode[]) {
  activeNodes.push(...nodes);
}

function trackTimer(id: ReturnType<typeof setTimeout>) {
  activeTimers.push(id);
}

function clearTimers() {
  for (const id of activeTimers) clearTimeout(id);
  activeTimers = [];
}

function stopNodes() {
  clearTimers();
  for (const node of activeNodes) {
    try {
      if ('stop' in node && typeof (node as OscillatorNode).stop === 'function') {
        (node as OscillatorNode).stop();
      }
      node.disconnect();
    } catch {
      // already stopped
    }
  }
  activeNodes = [];
}

function makeNoiseBuffer(ctx: AudioContext, seconds = 3, pink = true): AudioBuffer {
  const len = ctx.sampleRate * seconds;
  const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    if (pink) {
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.969 * b2 + white * 0.153852;
      b3 = 0.8665 * b3 + white * 0.3104856;
      b4 = 0.55 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.016898;
      data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    } else {
      data[i] = white * 0.35;
    }
  }
  return buffer;
}

function connectNoise(
  ctx: AudioContext,
  buffer: AudioBuffer,
  opts: { filterHz: number; gain?: number; filterType?: BiquadFilterType; q?: number },
) {
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = opts.filterType ?? 'lowpass';
  filter.frequency.value = opts.filterHz;
  if (opts.q != null) filter.Q.value = opts.q;
  const gainN = ctx.createGain();
  gainN.gain.value = opts.gain ?? 0.12;
  source.connect(filter);
  filter.connect(gainN);
  gainN.connect(masterGain!);
  source.start();
  trackNode(source, filter, gainN);
  return { source, filter, gainN };
}

function addDrone(ctx: AudioContext, freq: number, gain = 0.04, type: OscillatorType = 'sine') {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  const gainN = ctx.createGain();
  gainN.gain.value = gain;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 800;
  osc.connect(filter);
  filter.connect(gainN);
  gainN.connect(masterGain!);
  osc.start();
  trackNode(osc, filter, gainN);
}

function scheduleRepeating(
  ctx: AudioContext,
  fn: () => void,
  minMs: number,
  maxMs: number,
  typeGuard: AmbientSoundType,
) {
  const run = () => {
    if (currentType !== typeGuard || !masterGain) return;
    fn();
    const delay = minMs + Math.random() * (maxMs - minMs);
    trackTimer(setTimeout(run, delay));
  };
  run();
}

function buildSound(type: AmbientSoundType) {
  const ctx = getCtx();
  const buffer = makeNoiseBuffer(ctx);

  switch (type) {
    case 'white-noise':
      connectNoise(ctx, makeNoiseBuffer(ctx, 2, false), { filterHz: 6000, gain: 0.08 });
      break;

    case 'rain':
      connectNoise(ctx, buffer, { filterHz: 1400, gain: 0.14, q: 0.7 });
      connectNoise(ctx, makeNoiseBuffer(ctx), { filterHz: 400, gain: 0.04, filterType: 'bandpass', q: 2 });
      break;

    case 'ocean': {
      const { gainN } = connectNoise(ctx, buffer, { filterHz: 500, gain: 0.1 });
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.06;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.05;
      lfo.connect(lfoGain);
      lfoGain.connect(gainN.gain);
      lfo.start();
      trackNode(lfo, lfoGain);
      break;
    }

    case 'stream': {
      connectNoise(ctx, buffer, { filterHz: 2200, gain: 0.1, q: 1.2 });
      connectNoise(ctx, makeNoiseBuffer(ctx), { filterHz: 3500, gain: 0.03, filterType: 'highpass' });
      scheduleRepeating(
        ctx,
        () => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = 1200 + Math.random() * 600;
          const env = ctx.createGain();
          env.gain.value = 0;
          const now = ctx.currentTime;
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.008, now + 0.02);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
          osc.connect(env);
          env.connect(masterGain!);
          osc.start(now);
          osc.stop(now + 0.3);
          trackNode(osc, env);
        },
        300,
        900,
        'stream',
      );
      break;
    }

    case 'forest': {
      connectNoise(ctx, buffer, { filterHz: 1800, gain: 0.05 });
      addDrone(ctx, 110, 0.012, 'triangle');
      scheduleRepeating(
        ctx,
        () => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = 1400 + Math.random() * 800;
          const env = ctx.createGain();
          env.gain.value = 0;
          const now = ctx.currentTime;
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.012, now + 0.04);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
          osc.connect(env);
          env.connect(masterGain!);
          osc.start(now);
          osc.stop(now + 0.55);
          trackNode(osc, env);
        },
        2000,
        6000,
        'forest',
      );
      break;
    }

    case 'wind': {
      const { filter } = connectNoise(ctx, buffer, { filterHz: 400, gain: 0.09 });
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.03;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 250;
      lfo.connect(lfoGain);
      lfoGain.connect(filter.frequency);
      lfo.start();
      trackNode(lfo, lfoGain);
      break;
    }

    case 'fireplace': {
      const { gainN } = connectNoise(ctx, buffer, { filterHz: 300, gain: 0.06 });
      addDrone(ctx, 60, 0.02, 'sine');
      scheduleRepeating(
        ctx,
        () => {
          const osc = ctx.createOscillator();
          osc.type = 'triangle';
          osc.frequency.value = 80 + Math.random() * 120;
          const env = ctx.createGain();
          env.gain.value = 0;
          const now = ctx.currentTime;
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.025, now + 0.01);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
          osc.connect(env);
          env.connect(masterGain!);
          osc.start(now);
          osc.stop(now + 0.2);
          trackNode(osc, env);
          gainN.gain.setValueAtTime(gainN.gain.value, now);
          gainN.gain.linearRampToValueAtTime(0.08, now + 0.05);
          gainN.gain.linearRampToValueAtTime(0.06, now + 0.3);
        },
        400,
        1800,
        'fireplace',
      );
      break;
    }

    case 'meditation': {
      const freqs = [174.61, 261.63, 392.0];
      for (const f of freqs) {
        addDrone(ctx, f, 0.025, 'sine');
      }
      scheduleRepeating(
        ctx,
        () => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = 432 + Math.random() * 20;
          const env = ctx.createGain();
          env.gain.value = 0;
          const now = ctx.currentTime;
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.06, now + 0.8);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 6);
          osc.connect(env);
          env.connect(masterGain!);
          osc.start(now);
          osc.stop(now + 6.5);
          trackNode(osc, env);
        },
        8000,
        14000,
        'meditation',
      );
      break;
    }

    case 'night': {
      connectNoise(ctx, buffer, { filterHz: 800, gain: 0.02 });
      scheduleRepeating(
        ctx,
        () => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = 4200 + Math.random() * 800;
          const env = ctx.createGain();
          env.gain.value = 0;
          const now = ctx.currentTime;
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.006, now + 0.02);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
          osc.connect(env);
          env.connect(masterGain!);
          osc.start(now);
          osc.stop(now + 0.15);
          trackNode(osc, env);
        },
        600,
        2200,
        'night',
      );
      scheduleRepeating(
        ctx,
        () => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = 3800 + Math.random() * 400;
          const env = ctx.createGain();
          env.gain.value = 0;
          const now = ctx.currentTime;
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.004, now + 0.015);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
          osc.connect(env);
          env.connect(masterGain!);
          osc.start(now);
          osc.stop(now + 0.12);
          trackNode(osc, env);
        },
        700,
        2500,
        'night',
      );
      break;
    }

    case 'piano': {
      const chord = [261.63, 329.63, 392.0, 493.88];
      scheduleRepeating(
        ctx,
        () => {
          const note = chord[Math.floor(Math.random() * chord.length)];
          const osc = ctx.createOscillator();
          osc.type = 'triangle';
          osc.frequency.value = note;
          const env = ctx.createGain();
          env.gain.value = 0;
          const now = ctx.currentTime;
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.04, now + 0.15);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 4);
          const filter = ctx.createBiquadFilter();
          filter.type = 'lowpass';
          filter.frequency.value = 1200;
          osc.connect(filter);
          filter.connect(env);
          env.connect(masterGain!);
          osc.start(now);
          osc.stop(now + 4.5);
          trackNode(osc, filter, env);
        },
        2500,
        5000,
        'piano',
      );
      connectNoise(ctx, buffer, { filterHz: 600, gain: 0.015 });
      break;
    }

    case 'cafe': {
      connectNoise(ctx, buffer, { filterHz: 700, gain: 0.06 });
      addDrone(ctx, 50, 0.01, 'sine');
      scheduleRepeating(
        ctx,
        () => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = 200 + Math.random() * 100;
          const env = ctx.createGain();
          env.gain.value = 0;
          const now = ctx.currentTime;
          env.gain.setValueAtTime(0, now);
          env.gain.linearRampToValueAtTime(0.008, now + 0.02);
          env.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
          osc.connect(env);
          env.connect(masterGain!);
          osc.start(now);
          osc.stop(now + 0.25);
          trackNode(osc, env);
        },
        3000,
        8000,
        'cafe',
      );
      break;
    }
  }
}

async function fadeMasterGain(target: number, durationSec = 0.4): Promise<void> {
  if (!masterGain || !audioCtx) return;
  const now = audioCtx.currentTime;
  masterGain.gain.cancelScheduledValues(now);
  masterGain.gain.setValueAtTime(masterGain.gain.value, now);
  masterGain.gain.linearRampToValueAtTime(target, now + durationSec);
  if (target === 0) {
    await new Promise((r) => setTimeout(r, durationSec * 1000 + 50));
  }
}

export async function startAmbientSound(type: AmbientSoundType, fromPreview = false): Promise<void> {
  if (!fromPreview) previewMode = false;
  if (currentType === type && masterGain) return;
  await stopAmbientSound();
  const ctx = getCtx();
  await ctx.resume();
  masterGain = ctx.createGain();
  masterGain.gain.value = 0;
  masterGain.connect(ctx.destination);
  currentType = type;
  buildSound(type);
  await fadeMasterGain(0.32, 0.6);
}

/** Short preview when picking a sound in settings (~4 seconds). */
export async function previewAmbientSound(type: AmbientSoundType): Promise<void> {
  previewMode = true;
  await startAmbientSound(type, true);
  trackTimer(
    setTimeout(() => {
      if (previewMode) {
        previewMode = false;
        void stopAmbientSound();
      }
    }, 4000),
  );
}

export async function stopAmbientSound(): Promise<void> {
  if (masterGain && audioCtx) {
    await fadeMasterGain(0, 0.3);
  }
  stopNodes();
  if (masterGain) {
    masterGain.disconnect();
    masterGain = null;
  }
  currentType = null;
}

export function playSessionChime(kind: 'complete' | 'break' = 'complete'): void {
  try {
    const ctx = getCtx();
    void ctx.resume().then(() => {
      const notes = kind === 'complete' ? [523.25, 659.25, 783.99] : [440, 523.25];
      const now = ctx.currentTime;
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const start = now + i * 0.12;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.12, start + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.7);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.75);
      });
    });
  } catch {
    // audio unavailable
  }
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

export function showSessionNotification(title: string, body: string): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, icon: '/favicon.ico' });
  } catch {
    // ignore
  }
}
